/**
 * FinanceUploadPage — monthly xlsx upload admin page (Phase 5 Plan 04 Task 4).
 *
 * Route: `/admin/finance-upload`
 *
 * Implements UI-SPEC `/admin/finance-upload` page anatomy:
 *   - Page header + intro paragraph (Copywriting Contract verbatim).
 *   - "Download monthly template (.xlsx)" CTA → fetch + browser download.
 *   - 5-state drop zone:
 *       idle / dragging / uploading / success / error
 *   - Validation-error path: flat list of `{row, column, message}` cells (max 20).
 *   - 200 success: banner with computed_metrics preview (CAC / LTV:CAC / Marketing %).
 *   - 413 / 403 / 500: themed error banners with the UI-SPEC copy.
 *   - Upload history table (last 12 entries) via useFinanceUploadHistory.
 *
 * Defense-in-depth: useCroMe() exposes `groups` (Easy Auth claim). If the
 * caller is NOT in `saledash-finance-uploaders`, render an "Access restricted"
 * card per AdminTargetsPage pattern (mirror lines ~329-352 of that file).
 *
 * Note: Phase 04 useCroMe response shape only exposes `isCroGroupMember`, not
 * a generic per-group flag. The /api/cro/me response includes `groups: string[]`,
 * so we derive `isFinanceGroupMember` from that array client-side.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';

import { AppShell } from '../../components/AppShell.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { useCroMe } from '../../queries/useCroMe.js';
import { useFinanceUploadHistory } from '../../queries/useFinanceUploadHistory.js';
import {
  useFinanceUploadMutation,
  FinanceUploadValidationError,
  FinanceUploadFileTooLargeError,
  FinanceUploadForbiddenError,
  FinanceUploadServerError,
} from '../../queries/useFinanceUploadMutation.js';


import type {
  FinanceUploadCellError,
  FinanceUploadSuccessResponse,
} from '@saledash/shared';

// 2026-06-17 stakeholder decision: CRO group members get finance upload
// access by default ("if you have access to the CRO report you have access
// to the financial upload"). Both group object IDs are accepted; the
// previous check compared meData.groups (Entra GUIDs) against the literal
// string 'saledash-finance-uploaders' (a name), so it never matched.
const CRO_GROUP_ID = '42ffe548-b441-4dfb-9d36-63b13621d292';
const FINANCE_GROUPS_ALLOWED: readonly string[] = [CRO_GROUP_ID];
const MAX_FILE_BYTES = 1 * 1024 * 1024;

type State =
  | { kind: 'idle' }
  | { kind: 'dragging' }
  | { kind: 'uploading'; pct: number }
  | { kind: 'success'; response: FinanceUploadSuccessResponse }
  | { kind: 'error-validation'; errors: FinanceUploadCellError[] }
  | { kind: 'error-file-too-large' }
  | { kind: 'error-forbidden' }
  | { kind: 'error-server'; message: string };

/**
 * Format a number as a USD currency value with no fractional digits.
 */
function formatUsd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

/** Format an ISO date string as a readable "Jun 15, 2026" */
function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function FinanceUploadPage(): React.JSX.Element {
  const { data: meData, error: meError } = useCroMe();
  // 2026-06-17: useCroMe no longer auto-redirects on 401 (kept silent so
  // general /main users on apex aren't bounced into Entra sign-in they
  // don't have). Pages that strictly REQUIRE Easy Auth handle the bounce
  // here: if /api/cro/me returned 401, the user isn't signed in via
  // Entra — push them to /.auth/login/aad. Apex has this endpoint as of
  // 2026-06-17 (Easy Auth enabled with AllowAnonymous + CRO group gate).
  useEffect(() => {
    if (
      meError &&
      'status' in meError &&
      (meError as { status: number }).status === 401 &&
      typeof window !== 'undefined'
    ) {
      // Easy Auth honors `post_login_redirect_url` as the canonical param
      // on `/.auth/login/aad`; the `_uri` variant doesn't get honored on
      // initial sign-in and drops the user on a generic landing page. Use
      // an absolute same-origin URL so the redirect survives the AAD
      // round-trip even when the SPA's location.href includes a query
      // string (e.g. /admin/finance-upload?foo=bar).
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.assign(`/.auth/login/aad?post_login_redirect_url=${returnUrl}`);
    }
  }, [meError]);
  const { data: history, isLoading: historyLoading } = useFinanceUploadHistory();
  const lastUpload = history?.[0];
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [templateError, setTemplateError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Defense-in-depth: derive isFinanceGroupMember from the `groups` claim.
  // assume allowed until proven otherwise (avoids the empty flash on first render).
  // meData.groups holds Entra group OBJECT IDs (GUIDs) — see
  // easy-auth.ts parseGroupsFromPrincipalBase64. Match by GUID.
  const isFinanceGroupMember = useMemo(() => {
    if (!meData) return true;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- value comes from HTTP JSON / the browser DataTransfer; guard kept
    const userGroups = meData.groups ?? [];
    return FINANCE_GROUPS_ALLOWED.some((g) => userGroups.includes(g));
  }, [meData]);

  const mutation = useFinanceUploadMutation({
    onProgress: (pct: number) => {
      setState({ kind: 'uploading', pct });
    },
  });

  const handleFile = useCallback(
    (file: File | null | undefined): void => {
      if (!file) return;
      // Client-side preflight: reject non-.xlsx + over-size.
      if (!file.name.toLowerCase().endsWith('.xlsx')) {
        setState({
          kind: 'error-server',
          message: 'Please choose an .xlsx file.',
        });
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        setState({ kind: 'error-file-too-large' });
        return;
      }
      setState({ kind: 'uploading', pct: 0 });
      mutation.mutate(file, {
        onSuccess: (response) => { setState({ kind: 'success', response }); },
        onError: (err) => {
          if (err instanceof FinanceUploadValidationError) {
            setState({ kind: 'error-validation', errors: err.errors });
          } else if (err instanceof FinanceUploadFileTooLargeError) {
            setState({ kind: 'error-file-too-large' });
          } else if (err instanceof FinanceUploadForbiddenError) {
            setState({ kind: 'error-forbidden' });
          } else if (err instanceof FinanceUploadServerError) {
            setState({ kind: 'error-server', message: err.message });
          } else {
            setState({ kind: 'error-server', message: 'Unknown error.' });
          }
        },
      });
    },
    [mutation],
  );

  const handleDownloadTemplate = useCallback(async (): Promise<void> => {
    setTemplateError(null);
    try {
      const res = await fetch('/api/admin/finance-template', { credentials: 'include' });
      if (!res.ok) {
        setTemplateError(
          res.status === 403
            ? "Your account isn't in the CRO/Finance Entra group. Contact the CFO's office for access."
            : `Template download failed with status ${String(res.status)}.`,
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'adeptia-finance-monthly-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setTemplateError(`Template download failed: ${(err as Error).message}`);
    }
  }, []);

  // Drag-drop event handlers.
  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (state.kind === 'idle') setState({ kind: 'dragging' });
  }, [state.kind]);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    if (state.kind === 'dragging') setState({ kind: 'idle' });
  }, [state.kind]);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- value comes from HTTP JSON / the browser DataTransfer; guard kept
      const file = e.dataTransfer.files?.[0];
      handleFile(file);
    },
    [handleFile],
  );

  const onClickDropZone = useCallback((): void => {
    inputRef.current?.click();
  }, []);

  const resetToIdle = useCallback((): void => {
    setState({ kind: 'idle' });
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  // 403 fallback: not a member of the finance group.
  if (meData !== undefined && !isFinanceGroupMember) {
    return (
      <AppShell freshnessSlot={null}>
        <div
          className="flex min-h-[400px] items-center justify-center"
          data-testid="non-finance-member-fallback"
        >
          <Card className="max-w-md text-center space-y-4">
            <h2 className="text-base font-semibold text-ink-900">Access restricted</h2>
            <p className="text-sm text-ink-600">
              Your account is not in the CRO Entra group. Contact the{' '}
              <strong>CFO's office</strong> for access to the finance upload.
            </p>
            <Link
              to="/"
              className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
            >
              ← Back to scorecard
            </Link>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      freshnessSlot={null}
      brandSuffix="Finance Upload"
      crossLink={{ label: '← Main scorecard', to: '/main' }}
    >
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink-900">FINANCE UPLOAD</h1>
            <p className="mt-1 text-sm text-ink-500">
              Upload a filled monthly xlsx to refresh the ROI-tab tiles on /main.
              Download the template, fill the 11 columns, drop it back here. The
              same-month re-upload replaces prior values — no destructive UI.
            </p>
          </div>
          <Link
            to="/"
            className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
          >
            ← Scorecard
          </Link>
        </div>

        {/* Hidden file input — opened via the drop zone or click handler */}
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => { handleFile(e.target.files?.[0]); }}
        />

        {/* Top action row */}
        <Card>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-ink-600">
              {historyLoading
                ? 'Loading last upload…'
                : lastUpload
                  ? `Last upload: ${lastUpload.months_covered_csv || '—'} · uploaded by ${
                      lastUpload.uploaded_by
                    } · ${formatDate(lastUpload.uploaded_at)}`
                  : 'No uploads yet.'}
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleDownloadTemplate()}
              data-testid="download-template-btn"
            >
              Download monthly template (.xlsx)
            </Button>
          </div>
          {templateError !== null && (
            <p
              className="mt-3 text-xs text-bad-text"
              data-testid="template-download-error"
            >
              {templateError}
            </p>
          )}
        </Card>

        {/* Drop zone — renders one of the 5 states */}
        <div
          role="region"
          aria-label="Finance upload"
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={[
            'rounded-lg border-2 border-dashed transition-colors p-8 min-h-[200px]',
            state.kind === 'dragging' ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500' : '',
            state.kind === 'uploading' ? 'pointer-events-none opacity-60 border-ink-300' : '',
            state.kind === 'success' ? 'border-good bg-good-bg' : '',
            state.kind === 'error-validation' ||
            state.kind === 'error-file-too-large' ||
            state.kind === 'error-forbidden' ||
            state.kind === 'error-server'
              ? 'border-bad bg-bad-bg'
              : '',
            state.kind === 'idle' ? 'border-ink-300 hover:border-brand-400 cursor-pointer' : '',
          ].join(' ')}
          data-testid="drop-zone"
          data-state={state.kind}
          onClick={state.kind === 'idle' ? onClickDropZone : undefined}
        >
          {/* Idle state */}
          {state.kind === 'idle' && (
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-ink-700">
                Drop your filled monthly xlsx here, or click to browse
              </p>
              <p className="text-xs text-ink-500">Accepted: .xlsx · Max size 1 MB · One file at a time</p>
            </div>
          )}

          {/* Dragging state */}
          {state.kind === 'dragging' && (
            <div className="text-center space-y-2">
              <p className="text-sm font-semibold text-brand-700">Release to upload</p>
            </div>
          )}

          {/* Uploading state */}
          {state.kind === 'uploading' && (
            <div className="space-y-3">
              <p className="text-sm text-ink-700">Uploading…</p>
              <div className="w-full h-2 rounded bg-ink-200 overflow-hidden">
                <div
                  className="h-2 bg-brand-600 transition-[width]"
                  style={{ width: `${String(state.pct)}%` }}
                  data-testid="progress-bar"
                />
              </div>
              <p className="text-xs text-ink-500 tabular-nums">{state.pct}%</p>
            </div>
          )}

          {/* Success state */}
          {state.kind === 'success' && (
            <div className="space-y-3" data-testid="success-banner">
              <h3 className="text-sm font-semibold text-good-text">
                Upload successful — {state.response.rows_written} month
                {state.response.rows_written === 1 ? '' : 's'} written (
                {state.response.months_covered.join(', ')})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-ink-500">CAC</p>
                  <p className="tabular-nums font-semibold text-ink-900">
                    {formatUsd(state.response.computed_metrics.cac)}
                  </p>
                </div>
                <div>
                  <p className="text-ink-500">LTV : CAC</p>
                  <p className="tabular-nums font-semibold text-ink-900">
                    {state.response.computed_metrics.ltv_to_cac.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-ink-500">Marketing Spend % of CAC</p>
                  <p className="tabular-nums font-semibold text-ink-900">
                    {(state.response.computed_metrics.marketing_spend_pct_of_cac * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="primary" size="sm" onClick={resetToIdle}>
                  Upload another month
                </Button>
                <Link
                  to="/main?tab=roi"
                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"
                >
                  View ROI dashboard →
                </Link>
              </div>
            </div>
          )}

          {/* Validation error state */}
          {state.kind === 'error-validation' && (
            <div className="space-y-3" data-testid="error-validation-banner">
              <h3 className="text-sm font-semibold text-bad-text">
                Validation errors — please correct and re-upload
              </h3>
              <ul className="text-xs space-y-1 max-h-60 overflow-y-auto">
                {state.errors.slice(0, 20).map((e, i) => (
                  <li key={`${String(e.row)}-${e.column}-${String(i)}`} className="font-mono">
                    {e.row === 0
                      ? `Sheet: ${e.message}`
                      : `Row ${String(e.row)} / ${e.column}: ${e.message}`}
                  </li>
                ))}
                {state.errors.length > 20 && (
                  <li className="text-ink-500 italic">
                    …and {state.errors.length - 20} more — fix these first then re-upload.
                  </li>
                )}
              </ul>
              <Button variant="primary" size="sm" onClick={resetToIdle}>
                Upload corrected file
              </Button>
            </div>
          )}

          {/* 413 file-too-large state */}
          {state.kind === 'error-file-too-large' && (
            <div className="space-y-3" data-testid="error-file-too-large-banner">
              <h3 className="text-sm font-semibold text-bad-text">File too large</h3>
              <p className="text-xs text-bad-text">
                The xlsx must be 1 MB or smaller. Try removing extra sheets or formatting.
              </p>
              <Button variant="primary" size="sm" onClick={resetToIdle}>
                Try a smaller file
              </Button>
            </div>
          )}

          {/* 403 forbidden state */}
          {state.kind === 'error-forbidden' && (
            <div className="space-y-3" data-testid="error-forbidden-banner">
              <h3 className="text-sm font-semibold text-bad-text">Access denied</h3>
              <p className="text-xs text-bad-text">
                Your account isn't in the Finance role group. Contact the CFO's office for access.
              </p>
            </div>
          )}

          {/* 500 / generic server error state */}
          {state.kind === 'error-server' && (
            <div className="space-y-3" data-testid="error-server-banner">
              <h3 className="text-sm font-semibold text-bad-text">Upload failed</h3>
              <p className="text-xs text-bad-text">{state.message}</p>
              <Button variant="primary" size="sm" onClick={resetToIdle}>
                Try again
              </Button>
            </div>
          )}
        </div>

        {/* Upload history table (last 12 months) */}
        <Card className="p-0 overflow-hidden">
          <div className="border-b border-ink-200 bg-ink-50 px-4 py-3">
            <h2 className="text-sm font-semibold text-ink-900">Upload history</h2>
          </div>
          {historyLoading && (
            <div className="px-4 py-6 text-sm text-ink-500">Loading history…</div>
          )}
          {!historyLoading && history?.length === 0 && (
            <div className="px-4 py-6 text-sm text-ink-500">
              No uploads yet — Finance hasn't seeded any months.
            </div>
          )}
          {!historyLoading && history && history.length > 0 && (
            <table className="w-full text-sm" data-testid="history-table">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="px-4 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Month
                  </th>
                  <th className="px-4 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Uploaded
                  </th>
                  <th className="px-4 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Uploaded by
                  </th>
                  <th className="px-4 py-2 text-right text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Rows
                  </th>
                  <th className="px-4 py-2 text-left text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id} className="border-b border-ink-100 hover:bg-ink-50">
                    <td className="px-4 py-2 tabular-nums text-ink-900">
                      {row.months_covered_csv || '—'}
                    </td>
                    <td className="px-4 py-2 text-ink-600">{formatDate(row.uploaded_at)}</td>
                    <td className="px-4 py-2 text-ink-600">{row.uploaded_by}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-ink-900">
                      {row.rows_written}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={[
                          'inline-flex items-center rounded px-2 py-0.5 text-[0.7rem] font-semibold',
                          row.result === 'success' ? 'bg-good-bg text-good-text' : '',
                          row.result === 'validation_failed' ? 'bg-bad-bg text-bad-text' : '',
                          row.result === 'server_error' ? 'bg-ink-200 text-ink-700' : '',
                        ].join(' ')}
                      >
                        {row.result}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
